import { IconName } from '@ardoq/icons';
import { ContextToolBarAction, ContextToolBarActionIds } from './types';
import { ButtonType } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { contextToolBarCommands } from './contextToolBarCommands';
import { t, type I18nLocale } from '@ardoq/i18n';
import { createReferencesTracking } from '../sidebarPanel/sidebars/createReferences/createReferencesTracking';

const getIconColor = (isDisabled: boolean, isSelected: boolean) => {
  if (isSelected) {
    return colors.iconOnInverted;
  }
  if (isDisabled) {
    return colors.iconActionDisabled;
  }
  return colors.icon;
};

type ActionArgs = {
  i18nLocale: I18nLocale;
  isDisabled: boolean;
  isSelected: boolean;
};

export const getContextSwitcherAction = ({
  i18nLocale,
  isDisabled,
  isSelected,
}: ActionArgs): ContextToolBarAction => ({
  icon: IconName.LIST,
  iconColor: getIconColor(isDisabled, isSelected),
  buttonType: isSelected ? ButtonType.PRIMARY : ButtonType.GHOST,
  label: t(i18nLocale, 'canvas:actionLabelContext', 'Context'),
  disabled: isDisabled,
  selected: isSelected,
  onClick: contextToolBarCommands.toggleContextPanel,
});

export const getViewpointSwitcherAction = ({
  i18nLocale,
  isDisabled,
  isSelected,
}: ActionArgs): ContextToolBarAction => ({
  icon: IconName.VISIBILITY,
  iconColor: getIconColor(isDisabled, isSelected),
  buttonType: isSelected ? ButtonType.PRIMARY : ButtonType.GHOST,
  label: t(i18nLocale, 'canvas:actionLabelViewpoints', 'Viewpoints'),
  disabled: isDisabled,
  selected: isSelected,
  onClick: contextToolBarCommands.toggleViewpointsPanel,
});

export const getCreateReferencesAction = ({
  i18nLocale,
  isDisabled,
  isSelected,
}: ActionArgs): ContextToolBarAction => ({
  icon: IconName.ADD,
  iconColor: getIconColor(isDisabled, isSelected),
  buttonType: isSelected ? ButtonType.PRIMARY : ButtonType.GHOST,
  label: t(
    i18nLocale,
    'canvas:actionLabelCreateReferences',
    'Create references'
  ),
  disabled: isDisabled,
  selected: isSelected,
  onClick: () => {
    if (isSelected) {
      createReferencesTracking.trackClosePanel();
    } else {
      createReferencesTracking.trackOpenPanel('toolbar');
    }
    contextToolBarCommands.toggleCreateReferencesPanel();
  },
});

export const getDetailsAction = ({
  i18nLocale,
  isDisabled,
  isSelected,
}: ActionArgs): ContextToolBarAction => ({
  icon: IconName.DETAILS,
  iconColor: getIconColor(isDisabled, isSelected),
  buttonType: isSelected ? ButtonType.PRIMARY : ButtonType.GHOST,
  label: t(i18nLocale, 'canvas:actionLabelDetails', 'Details'),
  disabled: isDisabled,
  selected: isSelected,
  onClick: contextToolBarCommands.toggleDetailsPanel,
});

export const getDatasetsAction = ({
  i18nLocale,
  isDisabled,
  isSelected,
}: ActionArgs): ContextToolBarAction => ({
  icon: IconName.DATASET,
  iconColor: getIconColor(isDisabled, isSelected),
  buttonType: isSelected ? ButtonType.PRIMARY : ButtonType.GHOST,
  label: t(i18nLocale, 'canvas:actionLabelDatasets', 'Datasets'),
  disabled: isDisabled,
  selected: isSelected,
  onClick: contextToolBarCommands.toggleDatasetsPanel,
});

export const AdminActions = [
  ContextToolBarActionIds.CONTEXT_SWITCHER,
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.CREATE_REFERENCES,
  ContextToolBarActionIds.DETAILS,
  ContextToolBarActionIds.DATASETS,
];

export const WriterActions = [
  ContextToolBarActionIds.CONTEXT_SWITCHER,
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.CREATE_REFERENCES,
  ContextToolBarActionIds.DETAILS,
  ContextToolBarActionIds.DATASETS,
];

export const ReaderActions = [
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.DETAILS,
];

export const ContributorActions = [
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.DETAILS,
];

export const ActionsDisabledWhenNoComponentsSelected = new Set([
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.CREATE_REFERENCES,
  ContextToolBarActionIds.DETAILS,
]);

export const ActionsDisabledWhenMultipleTypesSelected = new Set([
  ContextToolBarActionIds.VIEWPOINT_SWITCHER,
  ContextToolBarActionIds.CREATE_REFERENCES,
  ContextToolBarActionIds.DETAILS,
]);

export const ActionsDisabledWhenMultipleComponentsSelected = new Set([
  ContextToolBarActionIds.DETAILS,
]);

export const ActionsDisabledWhenIsWildcardTraversal = new Set([]);
